import React, { useEffect } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import NoWorkspaceScreen from "./NoWorkspaceScreen";
import WorkspacesLoadError from "./WorkspacesLoadError";

// Extracted from App.js so these can be unit-tested without pulling in every
// page component App.js imports (Jest would otherwise need to transitively
// render/mock the whole route tree just to test a redirect).

// Resolves the :clientUuid URL segment to a workspace, keeps the active
// workspace in sync with the URL (the URL is the source of truth), and bounces
// unknown/unauthorized UUIDs to the user's default workspace. Rendered inside
// the persistent Layout, so its <Outlet /> is the page.
//
// In practice PrivateLayoutRoute (App.js) already renders NoWorkspaceScreen
// before Layout mounts when there are zero live workspaces, so the
// `workspaces.length === 0` branch below is defense-in-depth rather than the
// primary path — it exists so this gate is correct even if reused somewhere
// that isn't behind that check.
export const WorkspaceGate = () => {
  const { clientUuid } = useParams();
  const { workspaces, currentWorkspace, switchWorkspace, isInitialized, workspacesLoaded, workspacesError } = useAuth();

  const target = workspaces.find((w) => w.uuid === clientUuid);

  useEffect(() => {
    if (target && currentWorkspace?.uuid !== clientUuid) {
      // Optimistic — sets currentWorkspace immediately, then syncs the backend
      // default + /me/access so user.client_id follows the URL.
      switchWorkspace(target);
    }
  }, [clientUuid, target, currentWorkspace?.uuid, switchWorkspace]);

  if (!isInitialized) return null;

  // A failed GET /workspaces must NOT be read as "confirmed zero
  // workspaces" — that would show owners a duplicate "create your first
  // workspace" form and wrongly tell members they have no access.
  if (workspacesError) return <WorkspacesLoadError />;

  // Wait for workspaces to load before judging the UUID.
  if (!workspacesLoaded) return null;

  if (workspaces.length === 0) return <NoWorkspaceScreen />;

  if (!target) {
    const fallback =
      currentWorkspace || workspaces.find((w) => w.is_default) || workspaces[0];
    return <Navigate to={`/${fallback.uuid}/dashboard`} replace />;
  }

  // Hold rendering until the active workspace matches the URL, so pages never
  // fire API calls against the previous workspace for a frame.
  if (currentWorkspace?.uuid !== clientUuid) return null;

  return <Outlet />;
};

// Workspace-less /settings/* URLs reach us when an external OAuth callback
// returns to a static, pre-registered redirect_uri that can't carry the
// per-workspace UUID (Slack's redirect URL is fixed at app-registration time,
// e.g. /settings/integrations?slack_callback=true). Bounce into the active
// workspace's settings page, PRESERVING the query string so the callback params
// (?slack_callback&code&state) survive and IntegrationsSettings can complete the
// connection instead of being dropped by the RootRedirect catch-all.
export const WorkspacelessSettingsRedirect = () => {
  const { section } = useParams();
  const location = useLocation();
  const { isAuthenticated, isInitialized, currentWorkspace, workspaces, workspacesLoaded, workspacesError } = useAuth();
  if (!isInitialized) return null;
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }
  if (workspacesError) return <WorkspacesLoadError />;
  if (!workspacesLoaded) return null; // workspaces still loading
  const ws =
    currentWorkspace || workspaces.find((w) => w.is_default) || workspaces[0];
  if (!ws) return <NoWorkspaceScreen />;
  return <Navigate to={`/${ws.uuid}/settings/${section}${location.search}`} replace />;
};

// Landing route ("/" and unmatched paths). Sends authenticated users to their
// default workspace dashboard; everyone else to /login. A signed-in user with
// zero live workspaces gets the no-workspace screen instead of a blank page.
export const RootRedirect = () => {
  const { isAuthenticated, isInitialized, currentWorkspace, workspaces, workspacesLoaded, workspacesError } = useAuth();
  if (!isInitialized) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (workspacesError) return <WorkspacesLoadError />;
  if (!workspacesLoaded) return null; // workspaces still loading
  const ws =
    currentWorkspace || workspaces.find((w) => w.is_default) || workspaces[0];
  if (!ws) return <NoWorkspaceScreen />;
  return <Navigate to={`/${ws.uuid}/dashboard`} replace />;
};
